import { IContact, IKeyFact, IProjectedContact, KeyDateKind } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { getBirthInfoFromKeyFact, isBirthdayKeyFact } from '../../../../models/BirthdayUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { baseStyleSheet } from '../../../styles/styles';
import { DeprecatedPopover, PopoverType } from '../../DeprecatedPopover';
import { KeyDateIcon } from '../../svgs/icons/KeyDateIcon';
import { styleSheet } from './styles';

interface IProps {
	keyFact: IKeyFact;
	contact?: IProjectedContact | IContact;
	onRemoveKeyDate?(): void;
	readonly?: boolean;
	textValueClassName?: string;
}

function _KeyFactWithKeyDate({ keyFact, contact, onRemoveKeyDate, readonly, textValueClassName }: IProps) {
	const userSession = useUserSession();
	const [showingKeyDatePopover, setShowingKeyDatePopover] = React.useState(false);
	const popoverTimeoutHandle = React.useRef(null);
	const days = userSession.account?.preferences?.renewalEmailDaysToLookAhead;
	const renewalLookAhead = days ? days.toLocaleString() : '30-60';

	const textBefore = keyFact.value.substring(0, keyFact.keyDate.span.offset);
	const dateText = keyFact.value.substring(
		keyFact.keyDate.span.offset,

		keyFact.keyDate.span.offset + keyFact.keyDate.span.length
	);

	const textAfter = keyFact.value.substring(keyFact.keyDate.span.offset + keyFact.keyDate.span.length);
	const clearPopoverTimeout = () => {
		if (popoverTimeoutHandle.current) {
			clearTimeout(popoverTimeoutHandle.current);
			popoverTimeoutHandle.current = null;
		}
	};
	const startPopoverTimeout = () => {
		clearPopoverTimeout();

		popoverTimeoutHandle.current = setTimeout(() => {
			toggleDisplayOfKeyDatePopover(false)();
			clearPopoverTimeout();
		}, 300);
	};
	const toggleDisplayOfKeyDatePopover = (_showingKeyDatePopover: boolean) => () => {
		if (showingKeyDatePopover) {
			clearPopoverTimeout();
		}
		setShowingKeyDatePopover(_showingKeyDatePopover);
		if (!_showingKeyDatePopover) {
			startPopoverTimeout();
		}
	};
	const onRemoveKeyDateClicked = () => {
		setShowingKeyDatePopover(false);
		onRemoveKeyDate?.();
	};

	const birthdayTooltipContent = React.useMemo(() => {
		if (!isBirthdayKeyFact(keyFact)) {
			return null;
		}
		const birthInfo = getBirthInfoFromKeyFact(keyFact);
		const salutationName = birthInfo?.name?.trim() || contact?.firstName?.trim() || 'the contact';
		return (
			<div>
				We will automatically remind you every year on this date. We&rsquo;ll use the name{' '}
				<strong>{salutationName}</strong> for birthday greetings. To change that name, delete this key fact and
				re-create it.
			</div>
		);
	}, [keyFact, contact]);

	return (
		<span className={textValueClassName || ''} id={keyFact.id}>
			<span>{textBefore}</span>
			<span className={css(styleSheet.keyDate)}>
				<span>{dateText}</span>
				<DeprecatedPopover
					anchor={
						<span
							className={css(styleSheet.keyDateIcon)}
							onMouseEnter={toggleDisplayOfKeyDatePopover(true)}
							onMouseLeave={readonly ? toggleDisplayOfKeyDatePopover(false) : startPopoverTimeout}
						>
							<KeyDateIcon kind={keyFact.keyDate.kind} />
						</span>
					}
					className='key-fact-key-date-popover'
					dismissOnClickOutside={true}
					isOpen={!!showingKeyDatePopover}
					onRequestClose={toggleDisplayOfKeyDatePopover(false)}
					preferredPlacement='above'
					type={PopoverType.white}
				>
					<div
						className={css(styleSheet.popoverContent)}
						onMouseEnter={clearPopoverTimeout}
						onMouseLeave={startPopoverTimeout}
					>
						{keyFact.keyDate.kind === KeyDateKind.Renewal ? (
							<div>
								We will automatically remind
								<br />
								{`you ${renewalLookAhead} days before this date.`}
							</div>
						) : birthdayTooltipContent ? (
							birthdayTooltipContent
						) : (
							<div>
								We will automatically remind
								<br />
								you every year on this date.
							</div>
						)}

						{!readonly && (
							<button
								className={css(baseStyleSheet.brandLink, styleSheet.popoverRemoveButton)}
								onClick={onRemoveKeyDateClicked}
							>
								<span>Remove</span>
							</button>
						)}
					</div>
				</DeprecatedPopover>
			</span>
			<span>{textAfter}</span>
		</span>
	);
}

export const KeyFactWithKeyDate = observer(_KeyFactWithKeyDate);
